import { useRef, useState } from "react";
import { toast } from "sonner";

import { queryClient } from "App";
import CustomTableService from "services/CustomTable";
import { formatError } from "routes/custom_tables/helpers";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ui/field";
import { Spinner } from "@/components/ui/spinner";

const DeleteColumnModal = ({
  open,
  onOpenChange,
  slug,
  fieldId,
  displayName,
}) => {
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const confirmInputRef = useRef(null);

  const handleClose = () => {
    if (loading) return;
    onOpenChange(false);
  };

  const matches = confirmText === displayName;

  const handleConfirm = async () => {
    if (!matches || !slug || !fieldId) return;
    setLoading(true);
    setError(null);
    try {
      await CustomTableService.deleteColumn(slug, fieldId);
      await queryClient.invalidateQueries({
        queryKey: ["custom-table-schema", slug],
      });
      toast.success("Field deleted");
      onOpenChange(false);
    } catch (err) {
      setError(formatError(err, "Failed to delete column"));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={o => {
        if (!o) handleClose();
      }}
    >
      <DialogContent aria-label="Delete column" initialFocus={confirmInputRef}>
        <DialogTitle>Delete column</DialogTitle>
        <div className="tw:flex tw:flex-col tw:gap-3">
          <p>
            You are about to permanently delete the column{" "}
            <strong>{displayName}</strong>. All values stored in this column
            will be removed. This cannot be undone.
          </p>
          <p className="tw:text-muted-foreground">
            Type the column name to confirm: <strong>{displayName}</strong>
          </p>
          <Field>
            <Input
              ref={confirmInputRef}
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder={displayName}
            />
          </Field>
          {error && (
            <div className="tw:text-sm tw:text-destructive">{error}</div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!matches || loading}
          >
            {loading && <Spinner data-icon="inline-start" />}
            Delete column
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteColumnModal;
